import React, { useState } from 'react';
import { Icons } from './Icons';
import { TabId } from '../types';

interface SidebarItemProps {
  id: TabId | null;
  icon: keyof typeof Icons;
  label: string;
  danger?: boolean;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onClickOverride?: () => void;
  afterClick?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ id, icon, label, danger, activeTab, onTabChange, onClickOverride, afterClick }) => {
  const IconComponent = Icons[icon];
  const handleClick = () => {
    if (onClickOverride) {
      onClickOverride();
      afterClick?.();
      return;
    }
    if (id) {
      onTabChange(id);
    }
    afterClick?.();
  };
  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center space-x-3 px-5 py-3.5 rounded-xl text-base transition-all ${activeTab === id
          ? 'bg-blue-600 text-white shadow-lg'
          : danger
            ? 'text-red-500 hover:bg-red-50'
            : 'text-slate-500 hover:bg-slate-100'
        }`}
    >
      <span className="scale-110">
        <IconComponent />
      </span>
      <span className="font-semibold">{label}</span>
    </button>
  );
};

interface SidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  clinicName: string;
  logoUrl?: string;
  onLogout: () => void;
  currentUserRole?: 'admin' | 'secretary' | 'doctor' | 'patient';
  onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, clinicName, logoUrl, onLogout, currentUserRole, onNavigate }) => {
  const fallbackLogo =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="16" fill="%231e3a8a"/><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" fill="white" font-family="Arial" font-weight="700" font-size="32">CS</text></svg>';
  const [imageSrc, setImageSrc] = useState<string>(logoUrl || fallbackLogo);
  const reminderLabel = currentUserRole === 'patient' ? 'My Notifications' : 'Reminder Queue';
  const patientsLabel = currentUserRole === 'patient' ? 'My Registry' : 'Patient Registry';
  const appointmentsLabel = currentUserRole === 'patient' ? 'My Appointments' : 'Appointments';
  const calendarLabel = currentUserRole === 'patient' ? 'My Calendar' : 'Calendar';
  const showReports = currentUserRole === 'admin' || currentUserRole === 'doctor';
  const showPayments = currentUserRole === 'admin' || currentUserRole === 'doctor';
  const showExpenses = currentUserRole === 'admin' || currentUserRole === 'doctor';
  const showPanoramic = currentUserRole !== 'patient';
  return (
    <aside className="w-full lg:w-72 bg-white border-r border-slate-200 flex flex-col p-3 lg:p-4 space-y-4 lg:space-y-5 lg:h-screen overflow-y-auto">
      <div className="flex items-center space-x-3 px-2">
        <img
          src={imageSrc}
          alt="Clinic logo"
          className="w-12 h-12 rounded-xl object-contain bg-slate-50 border"
          onError={() => setImageSrc(fallbackLogo)}
        />
        <h1 className="text-xl font-black text-slate-800 truncate" title={clinicName}>
          {clinicName || 'DentalCore'}
        </h1>
      </div>
      <nav className="flex-1 space-y-1 flex flex-col">
        <SidebarItem id="dashboard" icon="ShieldCheck" label="Dashboard" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        <SidebarItem id="patients" icon="Users" label={patientsLabel} activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        <SidebarItem id="appointments" icon="Clock" label={appointmentsLabel} activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        <SidebarItem id="followup" icon="Stethoscope" label="Follow-Up & Recall" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        <SidebarItem id="reminders" icon="Bell" label={reminderLabel} activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        <SidebarItem id="calendar" icon="Calendar" label={calendarLabel} activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
        {showPanoramic && <SidebarItem id="panoramic" icon="Image" label="Panoramic AI" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />}
        {showPayments && <SidebarItem id="payments" icon="DollarSign" label="Collections" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />}
        {showExpenses && <SidebarItem id="expenses" icon="FileText" label="Expenses" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />}
        {showReports && <SidebarItem id="reports" icon="FileText" label="Reports" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />}
        <SidebarItem id="settings" icon="Settings" label="Settings" activeTab={activeTab} onTabChange={onTabChange} afterClick={onNavigate} />
      </nav>
      <SidebarItem
        id={null}
        icon="LogOut"
        label="Log Out"
        danger
        activeTab={activeTab}
        onTabChange={onTabChange}
        onClickOverride={onLogout}
        afterClick={onNavigate}
      />
      <div className="px-2 text-[11px] text-slate-400">
        Powered by{' '}
        <a href="https://www.code2craft.com" className="hover:text-blue-600 underline">
          Code2Craft
        </a>
      </div>
    </aside>
  );
};
